import type { CogoBlockDef, CogoFlow } from "../core/types"
import type { ActionFlowEngine } from "./action-flow-engine"
import type { StateManager } from "./state-manager"

interface ModelContextTool {
  name: string
  description: string
  inputSchema?: Record<string, unknown>
  readOnly?: boolean
  execute(params: Record<string, unknown>): Promise<{
    content: Array<{ type: string; text: string }>
  }>
}

interface ModelContext {
  registerTool(tool: ModelContextTool): void
}

declare global {
  interface Navigator {
    modelContext?: ModelContext
  }
}

/**
 * Auto-register logic block flows as WebMCP tools.
 * Each exposed flow in a logic block becomes a navigator.modelContext tool.
 */
export function registerWebMCPTools(
  blocks: Record<string, CogoBlockDef>,
  flows: Record<string, CogoFlow>,
  actionEngine: ActionFlowEngine,
  stateManager: StateManager,
): void {
  if (typeof navigator === "undefined" || !navigator.modelContext) return

  const mc = navigator.modelContext

  for (const [, block] of Object.entries(blocks)) {
    const manifest = block.manifest
    if (!manifest) continue
    if (manifest.type === "ui") continue
    if (!manifest.webmcp?.expose) continue

    const ns = manifest.namespace
    if (!ns) continue

    for (const [toolName, toolMeta] of Object.entries(manifest.webmcp.tools)) {
      const flowKey = `${ns}.${toolName}`
      const flow = flows[flowKey]
      if (!flow) continue

      mc.registerTool({
        name: flowKey,
        description: toolMeta.description,
        inputSchema: block.variables?.context
          ? { type: "object", properties: block.variables.context }
          : undefined,
        readOnly: toolMeta.readOnly,

        async execute(params) {
          await actionEngine.executeFlow(flow, params)
          const state = stateManager.get(ns)
          return {
            content: [
              {
                type: "text",
                text: JSON.stringify(state),
              },
            ],
          }
        },
      })
    }
  }
}
